/* =========================================================================
   Fiena — single entry point for all section behaviours.
   Behaviours:
     - Hero gallery crossfade (5-frame, configurable interval).
     - FAQ accordion (one open at a time).
     - Parallax background translate (split-pair, landscape).
     - Topbar scroll observer (hide over hero, light scheme over cream).
   Theme-editor aware: cleans up on shopify:section:unload.
   ========================================================================= */
(function () {
  'use strict';

  // ---- Hero gallery -------------------------------------------------------

  function initGallery(root) {
    var frames = root.querySelectorAll('.fn-hero__frame');
    if (frames.length < 2) return;
    var interval = parseInt(root.getAttribute('data-interval'), 10) || 5000;
    var idx = 0;
    var timer = setInterval(function () {
      frames[idx].classList.remove('is-active');
      idx = (idx + 1) % frames.length;
      frames[idx].classList.add('is-active');
    }, interval);
    root.__fnGalleryTimer = timer;
  }

  function destroyGallery(root) {
    if (root && root.__fnGalleryTimer) {
      clearInterval(root.__fnGalleryTimer);
      root.__fnGalleryTimer = null;
    }
  }

  // ---- FAQ accordion ------------------------------------------------------

  function initFaq(root) {
    var items = root.querySelectorAll('.fn-faq__item');
    if (!items.length) return;

    items.forEach(function (item) {
      var q = item.querySelector('.fn-faq__q');
      if (!q || q.__fnBound) return;
      q.__fnBound = true;
      q.addEventListener('click', function () {
        var wasOpen = item.classList.contains('is-open');
        items.forEach(function (i) {
          i.classList.remove('is-open');
          var b = i.querySelector('.fn-faq__q');
          if (b) b.setAttribute('aria-expanded', 'false');
        });
        if (!wasOpen) {
          item.classList.add('is-open');
          q.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  // ---- Parallax -----------------------------------------------------------
  // Single page-wide observer; updates every .fn-parallax via rAF on scroll.

  var parallaxItems = [];
  var parallaxTicking = false;
  var parallaxBound = false;

  function refreshParallax() {
    parallaxItems = Array.prototype.slice.call(document.querySelectorAll('.fn-parallax'));
  }

  function updateParallax() {
    var vh = window.innerHeight;
    parallaxItems.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) return;
      var center = rect.top + rect.height / 2;
      var off = (center - vh / 2) / vh;
      var amount = parseInt(el.getAttribute('data-parallax'), 10);
      if (isNaN(amount)) amount = 60;
      el.style.transform = 'translate3d(0,' + (-off * amount) + 'px,0)';
    });
  }

  function onParallaxScroll() {
    if (parallaxTicking) return;
    parallaxTicking = true;
    requestAnimationFrame(function () { updateParallax(); parallaxTicking = false; });
  }

  function bindParallax() {
    if (parallaxBound) return;
    parallaxBound = true;
    window.addEventListener('scroll', onParallaxScroll, { passive: true });
    window.addEventListener('resize', updateParallax);
  }

  // ---- Topbar scroll state ------------------------------------------------
  // Single page-wide observer. Hides while inside the hero; switches to
  // light colour scheme when scrolled over a cream/light section.

  var topbarBound = false;
  var topbarState = { hidden: false, light: false };

  function checkTopbar() {
    var topbar = document.querySelector('.fn-topbar');
    if (!topbar) return;

    var hero = document.querySelector('[data-fn-hero]');
    var lightZones = document.querySelectorAll('[data-fn-light-zone]');

    var shouldHide = false;
    if (hero) {
      var heroBottom = hero.getBoundingClientRect().bottom;
      shouldHide = heroBottom > window.innerHeight * 0.4;
    }
    if (shouldHide !== topbarState.hidden) {
      topbar.classList.toggle('is-hidden', shouldHide);
      topbarState.hidden = shouldHide;
    }

    var shouldLight = false;
    lightZones.forEach(function (zone) {
      var r = zone.getBoundingClientRect();
      if (r.top < 60 && r.bottom > 60) shouldLight = true;
    });
    if (shouldLight !== topbarState.light) {
      topbar.classList.toggle('is-light', shouldLight);
      topbarState.light = shouldLight;
    }
  }

  function bindTopbar() {
    if (topbarBound) return;
    topbarBound = true;
    window.addEventListener('scroll', checkTopbar, { passive: true });
    window.addEventListener('resize', checkTopbar);
  }

  // ---- Nav header overlay -------------------------------------------------
  // Hamburger in the bar opens a fullscreen overlay listing the nav menu.
  // Body-scroll lock, ESC + backdrop close, focus trap to the overlay.

  function initNavHeader(root) {
    var toggle  = root.querySelector('[data-fn-nav-toggle]');
    var overlay = root.querySelector('[data-fn-nav-overlay]');
    var closer  = root.querySelector('[data-fn-nav-close]');
    var backdrop = root.querySelector('[data-fn-nav-backdrop]');
    if (!toggle || !overlay) return;

    function open() {
      root.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      root.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
    function onKey(e) {
      if (e.key === 'Escape' && root.classList.contains('is-open')) close();
    }

    toggle.addEventListener('click', function () {
      if (root.classList.contains('is-open')) { close(); } else { open(); }
    });
    if (closer)   closer.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);
    document.addEventListener('keydown', onKey);

    root.__fnNavCleanup = function () {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }

  function destroyNavHeader(root) {
    if (root && root.__fnNavCleanup) {
      root.__fnNavCleanup();
      root.__fnNavCleanup = null;
    }
  }

  // ---- Section init / destroy --------------------------------------------

  function initSection(root) {
    if (!root || root.__fnInit) return;
    root.__fnInit = true;

    if (root.matches('[data-fn-hero]'))         initGallery(root);
    if (root.matches('[data-fn-nav-header]'))   initNavHeader(root);
    if (root.querySelector('.fn-faq'))          initFaq(root);
    if (root.querySelector('.fn-parallax'))     { refreshParallax(); bindParallax(); updateParallax(); }
    if (root.matches('.fn-topbar') || root.querySelector('.fn-topbar')) {
      bindTopbar();
      checkTopbar();
    }
  }

  function destroySection(root) {
    if (!root) return;
    destroyGallery(root);
    destroyNavHeader(root);
    root.__fnInit = false;
    refreshParallax();
  }

  function initAll() {
    document.querySelectorAll('[data-fn-section]').forEach(initSection);
    // Topbar lives in header-group; not a fn section by itself but still needs observer.
    if (document.querySelector('.fn-topbar')) { bindTopbar(); checkTopbar(); }
    refreshParallax();
    if (parallaxItems.length) { bindParallax(); updateParallax(); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor lifecycle ---------------------------------------------------

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-fn-section]') || e.target.querySelector('.fn-topbar');
    if (root) initSection(root);
    refreshParallax();
    updateParallax();
    checkTopbar();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-fn-section]');
    if (root) destroySection(root);
    refreshParallax();
  });

  document.addEventListener('shopify:section:select', function (e) {
    var root = e.target.querySelector('[data-fn-section]');
    if (root) { destroySection(root); initSection(root); }
  });
})();
